import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../utils/appConfirm';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaBriefcase,
  FaCheckCircle,
  FaClock,
  FaDollarSign,
  FaEnvelope,
  FaHistory,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaQuestionCircle,
  FaTicketAlt,
  FaBook,
  FaShieldAlt,
  FaMobileAlt,
  FaDesktop,
  FaLaptop,
} from 'react-icons/fa';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { graphqlRequest } from '../utils/graphqlRequest';
import {
  readStoredUserRoles,
  isSuperAdminUser,
  shouldOpenAdminProfileHub,
} from '../utils/adminProfileRouting';
import {
  ADMIN_PORTAL_BASE,
  ADMIN_ROUTES,
  adminPath,
} from '../constants/adminPortal';
import { useAdminPageSearch } from '../utils/useAdminPageSearch';
import '../styles/AdminProfileDashboard.css';

const GET_MY_PROFILE = `
  query GetMyProfile {
    getMyProfile {
      id
      firstName
      lastName
      email
      contact
      address
      joiningDate
      jobTitle
      roles
      upload
      isActive
      branchId
    }
  }
`;

const GET_USER_PROFILE = `
  query GetUserProfile($userId: String!) {
    getUserProfile(userId: $userId) {
      id
      firstName
      lastName
      email
      contact
      address
      joiningDate
      jobTitle
      roles
      upload
      isActive
      branchId
    }
  }
`;

const GET_MY_COMPANY_BRANCHES = `
  query GetMyCompanyBranches {
    getMyCompanyBranches {
      id
      name
      location
      isActive
    }
  }
`;

const GET_MY_DEVICE_SESSIONS = `
  query GetMyDeviceSessions {
    getMyDeviceSessions {
      sessionId
      device
      type
      location
      lastActiveAt
      isCurrent
    }
  }
`;

const GET_USER_DEVICE_SESSIONS = `
  query GetUserDeviceSessions($userId: String!) {
    getUserDeviceSessions(userId: $userId) {
      sessionId
      device
      type
      location
      lastActiveAt
      isCurrent
    }
  }
`;

const GET_MY_LOGIN_HISTORY = `
  query GetMyLoginHistory {
    getMyLoginHistory {
      userId
      name
      email
      ipAddress
      device
      loginMethod
      success
      failureReason
      occurredAt
    }
  }
`;

const GET_USER_LOGIN_HISTORY = `
  query GetUserLoginHistory($userId: String!) {
    getUserLoginHistory(userId: $userId) {
      userId
      name
      email
      ipAddress
      device
      loginMethod
      success
      failureReason
      occurredAt
    }
  }
`;

const FIND_MY_TIMECARDS = `
  query FindMyTimeCards($userId: String!) {
    findTimeCards(userId: $userId) {
      id
      date
      timeIn
      timeOut
      duration
      notes
      approvalStatus
      otHours
    }
  }
`;

const MY_PAYSLIPS = `
  query MyPayslipsForProfile($employeeId: String!) {
    payslips(employeeId: $employeeId) {
      id
      payPeriodStart
      payPeriodEnd
      payDate
      totalHoursWorked
      grossPay
      taxAmount
      netPay
      status
    }
  }
`;

const GET_MY_PROFILE_NOTES = `
  query GetMyProfileNotes {
    getMyProfileNotes {
      id
      subjectUserId
      authorUserId
      body
      tag
      createdAt
      updatedAt
      authorName
      authorIsSuperAdmin
    }
  }
`;

const GET_USER_PROFILE_NOTES = `
  query GetUserProfileNotes($userId: String!) {
    getUserProfileNotes(userId: $userId) {
      id
      subjectUserId
      authorUserId
      body
      tag
      createdAt
      updatedAt
      authorName
      authorIsSuperAdmin
    }
  }
`;

const GET_LEAVE_REQUESTS = `
  query LeaveRequests {
    leaveRequests {
      id
      createdAt
      name
      employeeId
      leaveType
      duration
      startDate
      endDate
      hour
      reason
      status
      managerFeedback
      appliedDate
    }
  }
`;

const CREATE_LEAVE_REQUEST = `
  mutation CreateLeaveRequest($input: CreateLeaveRequestInput!) {
    createLeaveRequest(createLeaveRequestInput: $input) {
      id
      employeeId
      leaveType
      duration
      startDate
      endDate
      hour
      reason
      status
      appliedDate
    }
  }
`;

const APPROVE_LEAVE_REQUEST = `
  mutation ApproveLeaveRequest($id: String!) {
    updateLeaveRequestStatus(id: $id, status: "Approved") {
      id
      status
      managerFeedback
    }
  }
`;

const FIND_TIMECARDS_FOR_DATE = `
  query FindTimeCardsForDate($userId: String!, $date: String!) {
    findTimeCards(userId: $userId, date: $date) {
      id
      timeIn
      timeOut
      leaveType
      leaveHours
      notes
    }
  }
`;

const CREATE_TIMECARD = `
  mutation CreateTimeCard($input: CreateTimeCardInput!) {
    createTimeCard(createTimeCardInput: $input) {
      id
      date
      timeIn
      timeOut
      leaveType
      leaveHours
    }
  }
`;

const UPDATE_TIMECARD = `
  mutation UpdateTimeCard($input: UpdateTimeCardInput!) {
    updateTimeCard(updateTimeCardInput: $input) {
      id
      date
      timeIn
      timeOut
      leaveType
      leaveHours
    }
  }
`;

const CREATE_MY_PROFILE_NOTE = `
  mutation CreateMyProfileNote($input: CreateUserProfileNoteInput!) {
    createMyProfileNote(input: $input) {
      id
      body
      tag
      createdAt
      authorName
    }
  }
`;

const CREATE_USER_PROFILE_NOTE = `
  mutation CreateUserProfileNote($userId: String!, $input: CreateUserProfileNoteInput!) {
    createUserProfileNote(userId: $userId, input: $input) {
      id
      body
      tag
      createdAt
      authorName
    }
  }
`;

const DELETE_MY_PROFILE_NOTE = `
  mutation DeleteMyProfileNote($id: String!) {
    deleteMyProfileNote(id: $id)
  }
`;

const NOTE_TAGS = ['General', 'Important', 'Info'];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'login-history', label: 'Login History' },
  { id: 'timesheets', label: 'Timesheets' },
  { id: 'payroll', label: 'Payroll' },
  { id: 'notes', label: 'Notes' },
  { id: 'leave', label: 'Leave' },
];

const LOGIN_HISTORY_PAGE_SIZE = 10;

function initials(first, last) {
  const a = String(first || '')
    .trim()
    .charAt(0);
  const b = String(last || '')
    .trim()
    .charAt(0);
  const s = `${a}${b}`.toUpperCase();
  return s || '?';
}

function formatRoleBadge(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return 'Admin';
  const primary = roles.find((r) => {
    const n = String(r).toLowerCase();
    return n.includes('admin') || n.includes('manager');
  });
  return primary
    ? String(primary).replace(/_/g, ' ')
    : String(roles[0]).replace(/_/g, ' ');
}

function formatJoiningDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString();
}

function resolveUploadUrl(rawUpload) {
  if (!rawUpload) return '';
  if (typeof rawUpload === 'string') {
    const value = rawUpload.trim();
    if (!value) return '';
    if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:'))
      return value;
    if (value.startsWith('/')) return `${window.location.origin}${value}`;
    const bucket = process.env.REACT_APP_AWS_BUCKET_NAME;
    const region = process.env.REACT_APP_AWS_REGION;
    if (bucket && region) {
      return `https://${bucket}.s3.${region}.amazonaws.com/${value}`;
    }
    return '';
  }
  if (typeof rawUpload === 'object') {
    return rawUpload.url || rawUpload.dataUrl || rawUpload.key || '';
  }
  return '';
}

function deviceIcon(type) {
  if (type === 'mobile') return FaMobileAlt;
  if (type === 'desktop') return FaDesktop;
  return FaLaptop;
}

/** Rough parse of backend duration strings like "8 hours" / "7 hours 30 minutes". */
function parseDurationToHours(text) {
  if (!text || typeof text !== 'string') return 0;
  const lower = text.toLowerCase();
  let hours = 0;
  const hm = lower.match(/(\d+(?:\.\d+)?)\s*hour/);
  if (hm) hours += Number.parseFloat(hm[1]) || 0;
  const mm = lower.match(/(\d+)\s*minute/);
  if (mm) hours += parseInt(mm[1], 10) / 60;
  return Math.round(hours * 100) / 100;
}

function formatMoneyAud(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 2,
    }).format(Number(n));
  } catch {
    return `$${Number(n).toFixed(2)}`;
  }
}

const CLOCK_OPEN = '[CLOCK_OPEN]';
const APD_LEAVE_TAG = '[APD_LEAVE]';

export default function AdminProfileDashboard() {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { get, patch } = useAdminPageSearch();
  const rawTab = get('tab') === 'activity-log' ? 'login-history' : get('tab');
  const tab = TABS.some((item) => item.id === rawTab) ? rawTab : 'overview';
  const setTab = (id) =>
    patch({
      tab: id === 'overview' ? '' : id,
      ...(id === 'notes' ? {} : { noteId: '' }),
    });
  const [profile, setProfile] = useState(null);
  const [actorProfile, setActorProfile] = useState(null);
  const [branchMap, setBranchMap] = useState(() => new Map());
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryPage, setLoginHistoryPage] = useState(1);
  const [timeCards, setTimeCards] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [timecardError, setTimecardError] = useState('');
  const [payslipError, setPayslipError] = useState('');
  const [profileNotes, setProfileNotes] = useState([]);
  const [notesError, setNotesError] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteTag, setNoteTag] = useState('General');
  const [noteSaving, setNoteSaving] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveError, setLeaveError] = useState('');
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    leaveType: 'Annual Leave',
    durationType: 'single', // single | multiple | hour
    date: '',
    startDate: '',
    endDate: '',
    hours: '',
    reason: '',
  });

  const currentUserId = useMemo(() => {
    const token = Cookies.get('accessToken');
    if (!token) return '';
    try {
      const decoded = jwtDecode(token);
      return String(decoded?.sub?.userId || '');
    } catch {
      return '';
    }
  }, []);

  const storedRoles = useMemo(() => readStoredUserRoles(), []);
  const isSuperAdmin = useMemo(
    () => isSuperAdminUser(storedRoles),
    [storedRoles],
  );

  const subjectUserId = useMemo(() => {
    const fromRoute = String(params?.userId || '').trim();
    if (fromRoute) return fromRoute;
    return currentUserId;
  }, [currentUserId, params?.userId]);

  const deepLink = useMemo(() => {
    const search = location?.search || '';
    const sp = new URLSearchParams(search);
    const tabParam = String(sp.get('tab') || '').trim();
    const noteId = String(sp.get('noteId') || '').trim();
    return {
      tab: tabParam,
      noteId,
    };
  }, [location?.search]);

  const isViewingOtherUser = useMemo(() => {
    return (
      !!subjectUserId && !!currentUserId && subjectUserId !== currentUserId
    );
  }, [currentUserId, subjectUserId]);

  const load = useCallback(async () => {
    setLoading(true);
    setHistoryError('');
    setTimecardError('');
    setPayslipError('');
    setNotesError('');
    setLeaveError('');
    const uid = subjectUserId;
    try {
      const profileQuery =
        isViewingOtherUser && isSuperAdmin
          ? graphqlRequest(GET_USER_PROFILE, { userId: uid })
          : graphqlRequest(GET_MY_PROFILE);

      const actorQuery =
        isViewingOtherUser && isSuperAdmin
          ? graphqlRequest(GET_MY_PROFILE)
          : null;

      const notesQuery =
        isViewingOtherUser && isSuperAdmin
          ? graphqlRequest(GET_USER_PROFILE_NOTES, { userId: uid })
          : graphqlRequest(GET_MY_PROFILE_NOTES);

      const requests = actorQuery
        ? [profileQuery, actorQuery, notesQuery]
        : [profileQuery, notesQuery];

      // Load login history + sessions for self OR (Super Admin viewing another user).
      if (isViewingOtherUser && isSuperAdmin) {
        requests.push(
          graphqlRequest(GET_USER_DEVICE_SESSIONS, { userId: uid }),
        );
        requests.push(graphqlRequest(GET_USER_LOGIN_HISTORY, { userId: uid }));
      } else if (!isViewingOtherUser) {
        requests.push(graphqlRequest(GET_MY_DEVICE_SESSIONS));
        requests.push(graphqlRequest(GET_MY_LOGIN_HISTORY));
      }

      if (uid) {
        requests.push(graphqlRequest(FIND_MY_TIMECARDS, { userId: uid }));
        requests.push(graphqlRequest(MY_PAYSLIPS, { employeeId: uid }));
        requests.push(graphqlRequest(GET_LEAVE_REQUESTS));
      }

      const results = await Promise.allSettled(requests);
      let i = 0;
      const profileRes = results[i++];
      const actorRes = actorQuery ? results[i++] : null;
      const notesRes = results[i++];
      const sessRes =
        !isViewingOtherUser || (isViewingOtherUser && isSuperAdmin)
          ? results[i++]
          : { status: 'skipped' };
      const histRes =
        !isViewingOtherUser || (isViewingOtherUser && isSuperAdmin)
          ? results[i++]
          : { status: 'skipped' };
      const cardsRes = uid ? results[i++] : { status: 'skipped' };
      const paysRes = uid ? results[i++] : { status: 'skipped' };
      const leavesRes = uid ? results[i++] : { status: 'skipped' };

      if (profileRes.status === 'fulfilled') {
        const nextProfile =
          profileRes.value?.getMyProfile ||
            profileRes.value?.getUserProfile ||
            null;
        setProfile(nextProfile);

        const nextBranchId = String(nextProfile?.branchId || '').trim();
        if (nextBranchId) {
          try {
            const branchesData = await graphqlRequest(GET_MY_COMPANY_BRANCHES);
            const rows = Array.isArray(branchesData?.getMyCompanyBranches)
              ? branchesData.getMyCompanyBranches
              : [];
            const m = new Map();
            for (const b of rows) {
              if (b?.id) m.set(String(b.id), b);
            }
            setBranchMap(m);
          } catch {
            // Best effort: branch label will fall back to "All Branches".
            setBranchMap(new Map());
          }
        } else {
          setBranchMap(new Map());
        }
      } else {
        setProfile(null);
        setBranchMap(new Map());
      }

      if (actorRes && actorRes.status === 'fulfilled') {
        setActorProfile(actorRes.value?.getMyProfile || null);
      } else if (actorQuery) {
        setActorProfile(null);
      }

      if (sessRes.status === 'fulfilled') {
        setSessions(
          Array.isArray(sessRes.value?.getMyDeviceSessions)
            ? sessRes.value.getMyDeviceSessions
            : Array.isArray(sessRes.value?.getUserDeviceSessions)
              ? sessRes.value.getUserDeviceSessions
              : [],
        );
      } else {
        setSessions([]);
      }

      if (histRes.status === 'fulfilled') {
        setLoginHistory(
          Array.isArray(histRes.value?.getMyLoginHistory)
            ? histRes.value.getMyLoginHistory
            : Array.isArray(histRes.value?.getUserLoginHistory)
              ? histRes.value.getUserLoginHistory
              : [],
        );
      } else if (histRes.status === 'rejected') {
        setLoginHistory([]);
        setHistoryError(
          histRes.reason?.message || 'Unable to load login history.',
        );
      } else {
        setLoginHistory([]);
      }

      if (notesRes.status === 'fulfilled') {
        setProfileNotes(
          Array.isArray(notesRes.value?.getMyProfileNotes)
            ? notesRes.value.getMyProfileNotes
            : Array.isArray(notesRes.value?.getUserProfileNotes)
              ? notesRes.value.getUserProfileNotes
              : [],
        );
      } else {
        setProfileNotes([]);
        setNotesError(notesRes.reason?.message || 'Unable to load notes.');
      }

      if (cardsRes.status === 'fulfilled') {
        setTimeCards(
          Array.isArray(cardsRes.value?.findTimeCards)
            ? cardsRes.value.findTimeCards
            : [],
        );
      } else if (cardsRes.status === 'rejected') {
        setTimeCards([]);
        setTimecardError(
          cardsRes.reason?.message || 'Unable to load your time cards.',
        );
      } else {
        setTimeCards([]);
      }

      if (paysRes.status === 'fulfilled') {
        setPayslips(
          Array.isArray(paysRes.value?.payslips) ? paysRes.value.payslips : [],
        );
      } else if (paysRes.status === 'rejected') {
        setPayslips([]);
        setPayslipError(
          paysRes.reason?.message || 'Unable to load your payslips.',
        );
      } else {
        setPayslips([]);
      }

      if (leavesRes.status === 'fulfilled') {
        const rows = Array.isArray(leavesRes.value?.leaveRequests)
          ? leavesRes.value.leaveRequests
          : [];
        setLeaveRequests(rows);
      } else if (leavesRes.status === 'rejected') {
        setLeaveRequests([]);
        setLeaveError(
          leavesRes.reason?.message || 'Unable to load leave requests.',
        );
      } else {
        setLeaveRequests([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, isViewingOtherUser, subjectUserId]);

  const refreshNotesOnly = useCallback(async () => {
    try {
      const data =
        isViewingOtherUser && isSuperAdmin
          ? await graphqlRequest(GET_USER_PROFILE_NOTES, {
              userId: subjectUserId,
            })
          : await graphqlRequest(GET_MY_PROFILE_NOTES);
      setProfileNotes(
        Array.isArray(data?.getMyProfileNotes)
          ? data.getMyProfileNotes
          : Array.isArray(data?.getUserProfileNotes)
            ? data.getUserProfileNotes
            : [],
      );
      setNotesError('');
    } catch (err) {
      setNotesError(err?.message || 'Could not refresh notes.');
    }
  }, [isSuperAdmin, isViewingOtherUser, subjectUserId]);

  const handleAddNote = async (e) => {
    e?.preventDefault?.();
    const text = String(noteBody || '').trim();
    if (!text || noteSaving) return;
    setNoteSaving(true);
    setNotesError('');
    try {
      if (isViewingOtherUser && isSuperAdmin) {
        await graphqlRequest(CREATE_USER_PROFILE_NOTE, {
          userId: subjectUserId,
          input: { body: text, tag: noteTag || 'General' },
        });
      } else {
        await graphqlRequest(CREATE_MY_PROFILE_NOTE, {
          input: { body: text, tag: noteTag || 'General' },
        });
      }
      setNoteBody('');
      setNoteTag('General');
      await refreshNotesOnly();
    } catch (err) {
      setNotesError(err?.message || 'Could not save note.');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleDeleteNote = async (id) => {
    if (!id || deletingNoteId) return;
    if (!await appConfirmMessage('Delete this note?')) return;
    setDeletingNoteId(id);
    setNotesError('');
    try {
      await graphqlRequest(DELETE_MY_PROFILE_NOTE, { id });
      await refreshNotesOnly();
    } catch (err) {
      setNotesError(err?.message || 'Could not delete note.');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const noteTagClass = (tag) => {
    const t = String(tag || '').toLowerCase();
    if (t === 'important') return 'apd-note-tag apd-note-tag--important';
    if (t === 'info') return 'apd-note-tag apd-note-tag--info';
    return 'apd-note-tag apd-note-tag--general';
  };

  useEffect(() => {
    const roles = storedRoles;
    // Super Admins are allowed to use this dashboard when coming from "View profile".
    if (!isSuperAdmin && !shouldOpenAdminProfileHub(roles)) {
      navigate(ADMIN_PORTAL_BASE, { replace: true });
      return;
    }
    load();
  }, [isSuperAdmin, isViewingOtherUser, load, navigate, storedRoles]);

  // Deep-link: highlight/scroll to a specific note.
  useEffect(() => {
    if (tab !== 'notes') return;
    if (!deepLink?.noteId) return;
    const id = deepLink.noteId;
    // Wait for notes to render.
    const t = window.setTimeout(() => {
      const el = document.getElementById(`apd-note-${id}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
    return () => window.clearTimeout(t);
  }, [deepLink?.noteId, profileNotes, tab]);

  const successfulLogins = useMemo(
    () => loginHistory.filter((row) => row?.success),
    [loginHistory],
  );

  const loginHistoryTotalPages = Math.max(
    1,
    Math.ceil(loginHistory.length / LOGIN_HISTORY_PAGE_SIZE),
  );
  const loginHistoryCurrentPage = Math.min(
    loginHistoryPage,
    loginHistoryTotalPages,
  );
  const loginHistoryPageRows = useMemo(() => {
    const start = (loginHistoryCurrentPage - 1) * LOGIN_HISTORY_PAGE_SIZE;
    return loginHistory.slice(start, start + LOGIN_HISTORY_PAGE_SIZE);
  }, [loginHistory, loginHistoryCurrentPage]);

  useEffect(() => {
    setLoginHistoryPage(1);
  }, [loginHistory.length]);

  const totalLoginCount = successfulLogins.length;
  const lastLogin = useMemo(() => {
    if (successfulLogins.length === 0) return null;
    const sorted = [...successfulLogins].sort((a, b) => {
      const ta = new Date(a?.occurredAt || 0).getTime();
      const tb = new Date(b?.occurredAt || 0).getTime();
      return tb - ta;
    });
    return sorted[0]?.occurredAt || null;
  }, [successfulLogins]);

  const totalHoursFromTimecards = useMemo(() => {
    let sum = 0;
    for (const tc of timeCards) {
      if (String(tc?.notes || '').includes(CLOCK_OPEN)) continue;
      sum += parseDurationToHours(tc?.duration);
    }
    const rounded = Math.round(sum * 10) / 10;
    return rounded > 0 ? rounded : null;
  }, [timeCards]);

  const totalHoursFromPayslips = useMemo(() => {
    let sum = 0;
    for (const p of payslips) {
      const h = Number(p?.totalHoursWorked);
      if (Number.isFinite(h)) sum += h;
    }
    const rounded = Math.round(sum * 10) / 10;
    return rounded > 0 ? rounded : null;
  }, [payslips]);

  const overviewHoursDisplay =
    totalHoursFromPayslips ?? totalHoursFromTimecards;

  const totalNetEarnings = useMemo(() => {
    let sum = 0;
    for (const p of payslips) {
      const n = Number(p?.netPay);
      if (Number.isFinite(n)) sum += n;
    }
    return sum > 0 ? sum : null;
  }, [payslips]);

  const displayName = useMemo(() => {
    const fromProfile =
      `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim();
    return fromProfile || profile?.email || Cookies.get('userName') || 'User';
  }, [profile?.firstName, profile?.lastName, profile?.email]);

  const actorDisplayName = useMemo(() => {
    const from =
      `${actorProfile?.firstName || ''} ${actorProfile?.lastName || ''}`.trim();
    return from || actorProfile?.email || Cookies.get('userName') || '';
  }, [actorProfile?.email, actorProfile?.firstName, actorProfile?.lastName]);

  const avatarUrl = resolveUploadUrl(profile?.upload);
  const roleBadge = formatRoleBadge(profile?.roles);
  const branchLabel = useMemo(() => {
    const bid = String(profile?.branchId || '').trim();
    if (!bid) return 'All Branches';
    const b = branchMap.get(bid);
    if (!b) return 'All Branches';
    const name = String(b?.name || '').trim();
    const loc = String(b?.location || '').trim();
    return (loc && name) ? `${loc} (${name})` : (loc || name || 'All Branches');
  }, [branchMap, profile?.branchId]);

  const leaveStatusClass = useCallback((status) => {
    const s = String(status || '')
      .trim()
      .toLowerCase();
    if (!s) return 'apd-badge-pending';
    if (s.includes('approve')) return 'apd-badge-success';
    if (s.includes('decline') || s.includes('reject')) return 'apd-badge-fail';
    if (s.includes('pending')) return 'apd-badge-pending';
    return 'apd-badge-pending';
  }, []);

  const filteredLeaveRequests = useMemo(() => {
    if (!subjectUserId) return [];
    return (leaveRequests || [])
      .filter((r) => String(r?.employeeId || '') === String(subjectUserId))
      .sort((a, b) => {
        const ta = new Date(
          a?.startDate || a?.appliedDate || a?.createdAt || 0,
        ).getTime();
        const tb = new Date(
          b?.startDate || b?.appliedDate || b?.createdAt || 0,
        ).getTime();
        return tb - ta;
      });
  }, [leaveRequests, subjectUserId]);

  const leaveDurationLabel = useCallback(() => {
    if (leaveForm.durationType === 'single') return '1 Day';
    if (leaveForm.durationType === 'hour') {
      return leaveForm.hours
        ? `${leaveForm.hours} Hour${Number(leaveForm.hours) === 1 ? '' : 's'}`
        : '';
    }
    if (leaveForm.durationType === 'multiple') {
      if (!leaveForm.startDate || !leaveForm.endDate) return '';
      const start = new Date(leaveForm.startDate);
      const end = new Date(leaveForm.endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
        return '';
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return `${diffDays} Day${diffDays !== 1 ? 's' : ''}`;
    }
    return '';
  }, [
    leaveForm.durationType,
    leaveForm.endDate,
    leaveForm.hours,
    leaveForm.startDate,
  ]);

  const toHHMM = useCallback((minutes) => {
    const normalized = ((minutes % (24 * 60)) + 24 * 60) % (24 * 60);
    const h = String(Math.floor(normalized / 60)).padStart(2, '0');
    const m = String(normalized % 60).padStart(2, '0');
    return `${h}:${m}`;
  }, []);

  const enumerateDatesInclusive = useCallback((startStr, endStr) => {
    if (!startStr || !endStr) return [];
    const start = new Date(`${startStr}T00:00:00`);
    const end = new Date(`${endStr}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    const out = [];
    const cursor = new Date(start);
    while (cursor.getTime() <= end.getTime() && out.length < 366) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, '0');
      const d = String(cursor.getDate()).padStart(2, '0');
      out.push(`${y}-${m}-${d}`);
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }, []);

  const upsertLeaveTimecards = useCallback(
    async (params) => {
      const {
        employeeId,
        leaveType,
        durationType,
        date,
        startDate,
        endDate,
        hours,
      } = params || {};
      const safeEmployeeId = String(employeeId || '').trim();
      if (!safeEmployeeId) return { createdOrUpdated: 0, skipped: [] };

      let dates = [];
      let leaveHoursPerDay = 8;

      if (durationType === 'single') {
        if (!date) return { createdOrUpdated: 0, skipped: [] };
        dates = [date];
        leaveHoursPerDay = 8;
      } else if (durationType === 'multiple') {
        dates = enumerateDatesInclusive(startDate, endDate);
        leaveHoursPerDay = 8;
      } else if (durationType === 'hour') {
        if (!date) return { createdOrUpdated: 0, skipped: [] };
        dates = [date];
        leaveHoursPerDay = Math.max(1, Math.min(8, Number(hours || 0)));
      }

      const skipped = [];
      let count = 0;

      // Default "leave shift" time window in the UI (09:00 start).
      const timeIn = toHHMM(9 * 60);
      const timeOut = toHHMM(9 * 60 + Math.round(leaveHoursPerDay * 60));
      const notes = `${APD_LEAVE_TAG} ${String(leaveType || '').trim()}`.trim();

      for (const day of dates) {
        const found = await graphqlRequest(FIND_TIMECARDS_FOR_DATE, {
          userId: safeEmployeeId,
          date: day,
        });
        const existing = Array.isArray(found?.findTimeCards)
          ? found.findTimeCards[0]
          : null;

        // If there is already a non-leave shift, don't overwrite it automatically.
        const existingHasWorkShift =
          existing &&
          (!existing.leaveType ||
            String(existing.leaveType).toLowerCase() === 'none') &&
          (existing.timeIn || existing.timeOut);

        if (existing && existingHasWorkShift) {
          skipped.push(day);
          continue;
        }

        if (existing?.id) {
          await graphqlRequest(UPDATE_TIMECARD, {
            input: {
              id: existing.id,
              timeIn,
              timeOut,
              leaveType,
              leaveHours: leaveHoursPerDay,
              notes,
            },
          });
          count += 1;
        } else {
          await graphqlRequest(CREATE_TIMECARD, {
            input: {
              userId: safeEmployeeId,
              date: day,
              timeIn,
              timeOut,
              leaveType,
              leaveHours: leaveHoursPerDay,
              notes,
            },
          });
          count += 1;
        }
      }

      return { createdOrUpdated: count, skipped };
    },
    [enumerateDatesInclusive, toHHMM],
  );

  const submitLeaveRequest = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (leaveSubmitting) return;
      setLeaveError('');

      const name = displayName || profile?.email || 'Employee';
      const employeeId = subjectUserId;
      const leaveType = leaveForm.leaveType;
      const reason = String(leaveForm.reason || '').trim();

      if (!employeeId) {
        setLeaveError('Missing employee id.');
        return;
      }
      if (!leaveType) {
        setLeaveError('Select a leave type.');
        return;
      }
      if (!reason) {
        setLeaveError('Please enter a reason.');
        return;
      }

      const duration = leaveDurationLabel();
      if (!duration) {
        setLeaveError('Please complete the duration fields.');
        return;
      }

      let startDate = '';
      let endDate = undefined;
      let hour = undefined;

      if (leaveForm.durationType === 'single') {
        if (!leaveForm.date) {
          setLeaveError('Please select a date.');
          return;
        }
        startDate = leaveForm.date;
      } else if (leaveForm.durationType === 'multiple') {
        if (!leaveForm.startDate || !leaveForm.endDate) {
          setLeaveError('Please select both start and end dates.');
          return;
        }
        startDate = leaveForm.startDate;
        endDate = leaveForm.endDate;
      } else if (leaveForm.durationType === 'hour') {
        if (!leaveForm.date) {
          setLeaveError('Please select a date.');
          return;
        }
        if (
          !leaveForm.hours ||
          Number(leaveForm.hours) < 1 ||
          Number(leaveForm.hours) > 8
        ) {
          setLeaveError('Hours must be between 1 and 8.');
          return;
        }
        startDate = leaveForm.date;
        hour = String(leaveForm.hours);
      }

      setLeaveSubmitting(true);
      try {
        const created = await graphqlRequest(CREATE_LEAVE_REQUEST, {
          input: {
            name,
            employeeId,
            leaveType,
            duration,
            startDate,
            ...(endDate ? { endDate } : {}),
            ...(hour ? { hour } : {}),
            reason,
          },
        });

        const row = created?.createLeaveRequest;
        if (!row?.id) {
          const fresh = await graphqlRequest(GET_LEAVE_REQUESTS);
          setLeaveRequests(
            Array.isArray(fresh?.leaveRequests) ? fresh.leaveRequests : [],
          );
        } else {
          // Admin portal behavior: auto-approve leave created here.
          // Backend defaults to Pending; we immediately set it to Approved.
          let approvedRow = row;
          try {
            const approved = await graphqlRequest(APPROVE_LEAVE_REQUEST, {
              id: row.id,
            });
            if (approved?.updateLeaveRequestStatus) {
              approvedRow = { ...row, ...approved.updateLeaveRequestStatus };
            }
          } catch {
            // If approval fails, still show the created request.
          }

          setLeaveRequests((prev) => [
            approvedRow,
            ...(Array.isArray(prev) ? prev : []),
          ]);

          // Option A: also generate TimeCard leave entries so Payroll → Time Tracking shows leave.
          try {
            const tc = await upsertLeaveTimecards({
              employeeId,
              leaveType,
              durationType: leaveForm.durationType,
              date: leaveForm.date,
              startDate: leaveForm.startDate,
              endDate: leaveForm.endDate,
              hours: leaveForm.hours,
            });
            if (tc?.skipped?.length) {
              setLeaveError(
                `Leave approved, but skipped ${tc.skipped.length} day(s) because a work timecard already exists (${tc.skipped.slice(0, 3).join(', ')}${
                  tc.skipped.length > 3 ? ', …' : ''
                }). Edit in Time Tracking if needed.`,
              );
            }
          } catch (tcErr) {
            setLeaveError(
              `Leave approved, but failed to create timecards for Time Tracking: ${
                tcErr?.message || 'unknown error'
              }`,
            );
          }
        }

        setLeaveForm({
          leaveType: 'Annual Leave',
          durationType: 'single',
          date: '',
          startDate: '',
          endDate: '',
          hours: '',
          reason: '',
        });
      } catch (err) {
        setLeaveError(err?.message || 'Could not submit leave request.');
      } finally {
        setLeaveSubmitting(false);
      }
    },
    [
      displayName,
      leaveDurationLabel,
      leaveForm,
      leaveSubmitting,
      profile?.email,
      subjectUserId,
      upsertLeaveTimecards,
    ],
  );

  if (loading && !profile) {
    return (
      <div className="apd-page apd-page--loading">
        <p className="apd-muted">Loading profile…</p>
      </div>
    );
  }

  return (
    <div className="apd-page">
      <button
        type="button"
        className="apd-back"
        onClick={() =>
          navigate(
            isSuperAdmin && isViewingOtherUser
              ? '/role-profiles'
              : ADMIN_PORTAL_BASE,
          )
        }
      >
        <FaArrowLeft aria-hidden />
        {isSuperAdmin && isViewingOtherUser
          ? 'Back to Role Profiles'
          : 'Back to dashboard'}
      </button>

      <div className="apd-layout">
        <aside className="apd-summary" aria-label="Profile summary">
          <h2 className="apd-summary-title">Profile Summary</h2>
          {isViewingOtherUser && isSuperAdmin ? (
            <p
              className="apd-muted"
              style={{ marginTop: -6, marginBottom: 14 }}
            >
              Viewing as <strong>{actorDisplayName || 'Super Admin'}</strong>
            </p>
          ) : null}
          <div className="apd-avatar-wrap">
            <div className="apd-avatar">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" />
              ) : (
                <span>{initials(profile?.firstName, profile?.lastName)}</span>
              )}
            </div>
          </div>
          <h3 className="apd-name">{displayName}</h3>
          <span className="apd-role-pill">{roleBadge}</span>

          <ul className="apd-meta">
            <li>
              <FaEnvelope aria-hidden />
              <div>
                <span className="apd-meta-label">Email</span>
                <span className="apd-meta-value">{profile?.email || '—'}</span>
              </div>
            </li>
            <li>
              <FaPhoneAlt aria-hidden />
              <div>
                <span className="apd-meta-label">Phone</span>
                <span className="apd-meta-value">
                  {profile?.contact || '—'}
                </span>
              </div>
            </li>
            <li>
              <FaMapMarkerAlt aria-hidden />
              <div>
                <span className="apd-meta-label">Branch</span>
                <span className="apd-meta-value">{branchLabel}</span>
              </div>
            </li>
            <li>
              <FaShieldAlt aria-hidden />
              <div>
                <span className="apd-meta-label">Joining Date</span>
                <span className="apd-meta-value">
                  {formatJoiningDate(profile?.joiningDate)}
                </span>
              </div>
            </li>
            <li>
              <FaCheckCircle aria-hidden />
              <div>
                <span className="apd-meta-label">Status</span>
                <span className="apd-status-pill">
                  {profile?.isActive === false ? 'Inactive' : 'Active'}
                </span>
              </div>
            </li>
          </ul>

          <div className="apd-stats-footer">
            <div>
              <span className="apd-stats-label">Total Login Count</span>
              <strong>{totalLoginCount.toLocaleString()}</strong>
            </div>
            <div>
              <span className="apd-stats-label">Last Login</span>
              <strong>
                {lastLogin
                  ? formatDateTime(lastLogin)
                  : sessions.some((s) => s?.isCurrent)
                    ? 'Active now'
                    : '—'}
              </strong>
            </div>
          </div>

          {!isViewingOtherUser ? (
            <Link className="apd-link-edit" to={adminPath('profile')}>
              Open full profile editor
            </Link>
          ) : null}
        </aside>

        <div className="apd-main">
          <nav
            className="apd-tabs"
            role="tablist"
            aria-label="Profile sections"
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={tab === t.id ? 'apd-tab apd-tab--active' : 'apd-tab'}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <div className="apd-panel" role="tabpanel">
            {tab === 'overview' && (
              <div className="apd-overview">
                <div className="apd-metric-grid">
                  <div className="apd-metric-card">
                    <FaClock className="apd-metric-icon" aria-hidden />
                    <span className="apd-metric-label">Total Hours</span>
                    <strong className="apd-metric-value">
                      {overviewHoursDisplay != null
                        ? `${overviewHoursDisplay.toLocaleString()}h`
                        : '—'}
                    </strong>
                    <span className="apd-metric-hint">
                      {totalHoursFromPayslips != null
                        ? 'Sum of hours on your payslips'
                        : 'From your submitted time cards'}
                    </span>
                  </div>
                  <div className="apd-metric-card">
                    <FaDollarSign className="apd-metric-icon" aria-hidden />
                    <span className="apd-metric-label">Total Earnings</span>
                    <strong className="apd-metric-value">
                      {totalNetEarnings != null
                        ? formatMoneyAud(totalNetEarnings)
                        : '—'}
                    </strong>
                    <span className="apd-metric-hint">
                      Net pay across your payslips
                    </span>
                  </div>
                  <div className="apd-metric-card">
                    <FaBriefcase className="apd-metric-icon" aria-hidden />
                    <span className="apd-metric-label">Active Projects</span>
                    <strong className="apd-metric-value">—</strong>
                    <span className="apd-metric-hint">Assigned work</span>
                  </div>
                  <div className="apd-metric-card">
                    <FaHistory className="apd-metric-icon" aria-hidden />
                    <span className="apd-metric-label">Successful logins</span>
                    <strong className="apd-metric-value">
                      {totalLoginCount > 0
                        ? totalLoginCount.toLocaleString()
                        : '—'}
                    </strong>
                    <span className="apd-metric-hint">
                      Recorded in login history
                    </span>
                  </div>
                </div>

                <section className="apd-help-card">
                  <h3>Help &amp; support</h3>
                  <p className="apd-muted">
                    Quick links for guides, policies, and getting help from the
                    team.
                  </p>
                  <ul className="apd-help-links">
                    <li>
                      <Link to={adminPath(ADMIN_ROUTES.HELP_SUPPORT)}>
                        <FaQuestionCircle aria-hidden />
                        Help &amp; Support center
                      </Link>
                    </li>
                    <li>
                      <Link to={adminPath(ADMIN_ROUTES.FAQ)}>
                        <FaBook aria-hidden />
                        FAQ
                      </Link>
                    </li>
                    <li>
                      <Link to={adminPath(ADMIN_ROUTES.REPORT_ISSUE)}>
                        <FaTicketAlt aria-hidden />
                        Report an issue
                      </Link>
                    </li>
                    <li>
                      <Link to={adminPath(ADMIN_ROUTES.NEW_TICKET)}>
                        <FaTicketAlt aria-hidden />
                        Create support ticket
                      </Link>
                    </li>
                    <li>
                      <Link to={adminPath(ADMIN_ROUTES.DEVICE_SESSIONS)}>
                        <FaShieldAlt aria-hidden />
                        Device sessions
                      </Link>
                    </li>
                  </ul>
                </section>

                <section className="apd-sessions-preview">
                  <h3>Recent devices</h3>
                  {sessions.length === 0 ? (
                    <p className="apd-muted">No session data yet.</p>
                  ) : (
                    <ul className="apd-session-list">
                      {sessions.slice(0, 4).map((s) => {
                        const Icon = deviceIcon(s?.type);
                        return (
                          <li key={s.sessionId}>
                            <Icon aria-hidden />
                            <div>
                              <strong>{s.device || 'Device'}</strong>
                              <small>
                                {s.location ? `${s.location} · ` : ''}
                                {s.isCurrent
                                  ? 'This device'
                                  : formatDateTime(s.lastActiveAt)}
                              </small>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {!isViewingOtherUser ? (
                    <Link
                      className="apd-link-edit"
                      to={adminPath(ADMIN_ROUTES.DEVICE_SESSIONS)}
                    >
                      Manage device sessions
                    </Link>
                  ) : null}
                </section>
              </div>
            )}

            {tab === 'login-history' && (
              <div className="apd-table-card">
                <h3>Login History</h3>
                {historyError ? (
                  <p className="apd-error">{historyError}</p>
                ) : loginHistory.length === 0 ? (
                  <p className="apd-muted">
                    No login records yet. Successful sign-ins are saved here
                    after you log in.
                  </p>
                ) : (
                  <>
                    <div className="apd-table-wrap">
                      <table className="apd-table">
                        <thead>
                          <tr>
                            <th>Portal</th>
                            <th>Login time</th>
                            <th>Device</th>
                            <th>IP</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginHistoryPageRows.map((row, idx) => {
                            const method = String(
                              row.loginMethod || '',
                            ).toLowerCase();
                            // Only true employee login methods — admin OTP also
                            // contains "otp" and must stay Admin portal.
                            const portal = method.includes('employee')
                              ? 'Employee portal'
                              : 'Admin portal';
                            const rowKey = `${row.occurredAt}-${(loginHistoryCurrentPage - 1) * LOGIN_HISTORY_PAGE_SIZE + idx}`;
                            return (
                              <tr key={rowKey}>
                                <td>{portal}</td>
                                <td>{formatDateTime(row.occurredAt)}</td>
                                <td>{row.device || '—'}</td>
                                <td>{row.ipAddress || '—'}</td>
                                <td>
                                  {row.success ? (
                                    <span className="apd-badge-success">
                                      Success
                                    </span>
                                  ) : (
                                    <span
                                      className="apd-badge-fail"
                                      title={row.failureReason || ''}
                                    >
                                      Failed
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {loginHistory.length > LOGIN_HISTORY_PAGE_SIZE ? (
                      <div className="apd-pagination">
                        <span>
                          Showing{' '}
                          {(loginHistoryCurrentPage - 1) *
                            LOGIN_HISTORY_PAGE_SIZE +
                            1}
                          –
                          {Math.min(
                            loginHistoryCurrentPage * LOGIN_HISTORY_PAGE_SIZE,
                            loginHistory.length,
                          )}{' '}
                          of {loginHistory.length}
                        </span>
                        <div className="apd-pagination-controls">
                          <button
                            type="button"
                            disabled={loginHistoryCurrentPage <= 1}
                            onClick={() =>
                              setLoginHistoryPage((p) => Math.max(1, p - 1))
                            }
                            aria-label="Previous page"
                          >
                            ‹
                          </button>
                          <span className="apd-pagination-page">
                            Page {loginHistoryCurrentPage} of{' '}
                            {loginHistoryTotalPages}
                          </span>
                          <button
                            type="button"
                            disabled={
                              loginHistoryCurrentPage >= loginHistoryTotalPages
                            }
                            onClick={() =>
                              setLoginHistoryPage((p) =>
                                Math.min(loginHistoryTotalPages, p + 1),
                              )
                            }
                            aria-label="Next page"
                          >
                            ›
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            )}

            {tab === 'timesheets' && (
              <div className="apd-table-card">
                <h3>Your timesheet records</h3>
                {timecardError ? (
                  <p className="apd-error">{timecardError}</p>
                ) : null}
                <p className="apd-muted">
                  Only your own time cards are shown.{' '}
                  <button
                    type="button"
                    className="apd-inline-link"
                    onClick={() =>
                      navigate(`${ADMIN_PORTAL_BASE}?page=time-card`)
                    }
                  >
                    Open Time Card
                  </button>
                </p>
                {timeCards.length === 0 && !timecardError ? (
                  <p className="apd-placeholder-note">
                    No time cards on file yet.
                  </p>
                ) : null}
                {timeCards.length > 0 ? (
                  <div className="apd-table-wrap">
                    <table className="apd-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Clock in</th>
                          <th>Clock out</th>
                          <th>Total hours</th>
                          <th>Overtime</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timeCards.slice(0, 80).map((tc) => (
                          <tr key={tc.id}>
                            <td>{tc.date || '—'}</td>
                            <td>{tc.timeIn || '—'}</td>
                            <td>
                              {String(tc.notes || '').includes(CLOCK_OPEN)
                                ? '— (open)'
                                : tc.timeOut || '—'}
                            </td>
                            <td>{tc.duration || '—'}</td>
                            <td>
                              {tc.otHours != null && Number(tc.otHours) > 0
                                ? `${Number(tc.otHours)}h`
                                : '—'}
                            </td>
                            <td>
                              <span
                                className={
                                  tc.approvalStatus === 'APPROVED'
                                    ? 'apd-badge-success'
                                    : tc.approvalStatus === 'REJECTED'
                                      ? 'apd-badge-fail'
                                      : 'apd-badge-pending'
                                }
                              >
                                {tc.approvalStatus || '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            )}

            {tab === 'payroll' && (
              <div className="apd-table-card">
                <h3>Your payroll history</h3>
                {payslipError ? (
                  <p className="apd-error">{payslipError}</p>
                ) : null}
                <p className="apd-muted">
                  Only payslips issued to your user account.{' '}
                </p>
                {payslips.length === 0 && !payslipError ? (
                  <p className="apd-placeholder-note">No payslips found yet.</p>
                ) : null}
                {payslips.length > 0 ? (
                  <div className="apd-table-wrap">
                    <table className="apd-table">
                      <thead>
                        <tr>
                          <th>Pay period</th>
                          <th>Total hours</th>
                          <th>Gross pay</th>
                          <th>Deductions (tax)</th>
                          <th>Net pay</th>
                          <th>Status</th>
                          <th>Pay date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payslips.slice(0, 50).map((p) => (
                          <tr key={p.id}>
                            <td>
                              {p.payPeriodStart && p.payPeriodEnd
                                ? `${String(p.payPeriodStart).slice(0, 7)}`
                                : '—'}
                            </td>
                            <td>
                              {Number(p.totalHoursWorked || 0).toLocaleString()}
                              h
                            </td>
                            <td>{formatMoneyAud(p.grossPay)}</td>
                            <td className="apd-num-neg">
                              {p.taxAmount != null
                                ? formatMoneyAud(p.taxAmount)
                                : '—'}
                            </td>
                            <td className="apd-num-pos">
                              {formatMoneyAud(p.netPay)}
                            </td>
                            <td>
                              <span className="apd-badge-pending">
                                {p.status || '—'}
                              </span>
                            </td>
                            <td>
                              {p.payDate ? String(p.payDate).slice(0, 10) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            )}

            {tab === 'notes' && (
              <div className="apd-table-card">
                <h3>Notes</h3>
                <p className="apd-muted">
                  Private notes on your profile (visible to you and stored for
                  your company account).
                </p>
                {notesError ? <p className="apd-error">{notesError}</p> : null}

                <form className="apd-note-form" onSubmit={handleAddNote}>
                  <label htmlFor="apd-note-body">Add note</label>
                  <textarea
                    id="apd-note-body"
                    rows={4}
                    placeholder="Add internal notes about your work, reminders, or context…"
                    value={noteBody}
                    onChange={(ev) => setNoteBody(ev.target.value)}
                    maxLength={8000}
                  />
                  <div className="apd-note-form-row">
                    <label
                      htmlFor="apd-note-tag"
                      className="apd-note-tag-label"
                    >
                      Tag
                    </label>
                    <select
                      id="apd-note-tag"
                      value={noteTag}
                      onChange={(ev) => setNoteTag(ev.target.value)}
                    >
                      {NOTE_TAGS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    <button
                      type="submit"
                      className="apd-note-submit"
                      disabled={noteSaving || !String(noteBody || '').trim()}
                    >
                      {noteSaving ? 'Saving…' : 'Add note'}
                    </button>
                  </div>
                </form>

                <h4 className="apd-note-history-title">Note history</h4>
                {profileNotes.length === 0 ? (
                  <p className="apd-placeholder-note">No notes yet.</p>
                ) : (
                  <ul className="apd-note-list">
                    {profileNotes.map((n) => (
                      <li
                        key={n.id}
                        id={`apd-note-${n.id}`}
                        className={`apd-note-item${
                          deepLink?.noteId && deepLink.noteId === n.id
                            ? ' apd-note-item--highlight'
                            : ''
                        }`}
                      >
                        <div className="apd-note-item-head">
                          <span className={noteTagClass(n.tag)}>
                            {n.tag || 'General'}
                          </span>
                          <time className="apd-note-time">
                            {formatDateTime(n.createdAt)}
                          </time>
                        </div>
                        <p className="apd-note-author">
                          {n.authorName || 'Unknown sender'}
                          {n.authorIsSuperAdmin ? (
                            <span className="apd-note-from-super">
                              From Super Admin
                            </span>
                          ) : null}
                        </p>
                        <p className="apd-note-body">{n.body}</p>
                        <button
                          type="button"
                          className="apd-note-delete"
                          disabled={deletingNoteId === n.id}
                          onClick={() => handleDeleteNote(n.id)}
                        >
                          {deletingNoteId === n.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === 'leave' && (
              <div className="apd-table-card">
                <h3>Leave</h3>
                <p className="apd-muted">
                  Apply leave for <strong>{displayName}</strong>.
                </p>
                {leaveError ? <p className="apd-error">{leaveError}</p> : null}

                <form
                  className="apd-note-form apd-leave-form"
                  onSubmit={submitLeaveRequest}
                >
                  <div className="apd-leave-grid">
                    <div className="apd-leave-col">
                      <label htmlFor="apd-leave-type">Leave type</label>
                      <select
                        id="apd-leave-type"
                        className="apd-leave-select"
                        value={leaveForm.leaveType}
                        onChange={(ev) =>
                          setLeaveForm((p) => ({
                            ...p,
                            leaveType: ev.target.value,
                          }))
                        }
                      >
                        <option value="Annual Leave">Annual Leave</option>
                        <option value="Sick Leave">Sick Leave</option>
                        <option value="Unpaid Leave">Unpaid Leave</option>
                        <option value="Compensation Leave">
                          Compensation Leave
                        </option>
                        <option value="Personal Leave">Personal Leave</option>
                      </select>
                    </div>

                    <div className="apd-leave-col apd-leave-col--action">
                      <button
                        type="submit"
                        className="apd-note-submit"
                        disabled={leaveSubmitting}
                      >
                        {leaveSubmitting ? 'Submitting…' : 'Apply leave'}
                      </button>
                    </div>
                  </div>

                  <div className="apd-leave-divider" />

                  <label className="apd-leave-label">Duration</label>
                  <div className="apd-leave-duration">
                    <label className="apd-leave-choice">
                      <input
                        type="radio"
                        name="apd-leave-duration"
                        value="single"
                        checked={leaveForm.durationType === 'single'}
                        onChange={() =>
                          setLeaveForm((p) => ({
                            ...p,
                            durationType: 'single',
                          }))
                        }
                      />
                      <span>Single day</span>
                    </label>
                    <label className="apd-leave-choice">
                      <input
                        type="radio"
                        name="apd-leave-duration"
                        value="multiple"
                        checked={leaveForm.durationType === 'multiple'}
                        onChange={() =>
                          setLeaveForm((p) => ({
                            ...p,
                            durationType: 'multiple',
                          }))
                        }
                      />
                      <span>Multiple days</span>
                    </label>
                    <label className="apd-leave-choice">
                      <input
                        type="radio"
                        name="apd-leave-duration"
                        value="hour"
                        checked={leaveForm.durationType === 'hour'}
                        onChange={() =>
                          setLeaveForm((p) => ({ ...p, durationType: 'hour' }))
                        }
                      />
                      <span>Hours</span>
                    </label>
                    <span className="apd-leave-hint">
                      {leaveDurationLabel()
                        ? `Selected: ${leaveDurationLabel()}`
                        : 'Choose dates to calculate'}
                    </span>
                  </div>

                  <div className="apd-leave-fields">
                    {leaveForm.durationType === 'single' && (
                      <div className="apd-leave-field">
                        <label htmlFor="apd-leave-date">Date</label>
                        <input
                          id="apd-leave-date"
                          className="apd-leave-input"
                          type="date"
                          value={leaveForm.date}
                          onChange={(ev) =>
                            setLeaveForm((p) => ({
                              ...p,
                              date: ev.target.value,
                            }))
                          }
                        />
                      </div>
                    )}

                    {leaveForm.durationType === 'multiple' && (
                      <>
                        <div className="apd-leave-field">
                          <label htmlFor="apd-leave-start">Start</label>
                          <input
                            id="apd-leave-start"
                            className="apd-leave-input"
                            type="date"
                            value={leaveForm.startDate}
                            onChange={(ev) =>
                              setLeaveForm((p) => ({
                                ...p,
                                startDate: ev.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="apd-leave-field">
                          <label htmlFor="apd-leave-end">End</label>
                          <input
                            id="apd-leave-end"
                            className="apd-leave-input"
                            type="date"
                            min={leaveForm.startDate || undefined}
                            value={leaveForm.endDate}
                            onChange={(ev) =>
                              setLeaveForm((p) => ({
                                ...p,
                                endDate: ev.target.value,
                              }))
                            }
                          />
                        </div>
                      </>
                    )}

                    {leaveForm.durationType === 'hour' && (
                      <>
                        <div className="apd-leave-field">
                          <label htmlFor="apd-leave-hour-date">Date</label>
                          <input
                            id="apd-leave-hour-date"
                            className="apd-leave-input"
                            type="date"
                            value={leaveForm.date}
                            onChange={(ev) =>
                              setLeaveForm((p) => ({
                                ...p,
                                date: ev.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="apd-leave-field">
                          <label htmlFor="apd-leave-hours">Hours (1–8)</label>
                          <input
                            id="apd-leave-hours"
                            className="apd-leave-input"
                            type="number"
                            min="1"
                            max="8"
                            value={leaveForm.hours}
                            onChange={(ev) =>
                              setLeaveForm((p) => ({
                                ...p,
                                hours: ev.target.value,
                              }))
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <label htmlFor="apd-leave-reason">Reason</label>
                  <textarea
                    id="apd-leave-reason"
                    rows={3}
                    placeholder="Reason for leave…"
                    value={leaveForm.reason}
                    onChange={(ev) =>
                      setLeaveForm((p) => ({ ...p, reason: ev.target.value }))
                    }
                    maxLength={2000}
                  />
                </form>

                <h4 className="apd-note-history-title">Leave requests</h4>
                {filteredLeaveRequests.length === 0 ? (
                  <p className="apd-placeholder-note">
                    No leave requests found for this user.
                  </p>
                ) : (
                  <div className="apd-table-wrap">
                    <table className="apd-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Start</th>
                          <th>End</th>
                          <th>Duration</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLeaveRequests.slice(0, 80).map((r) => (
                          <tr key={r.id}>
                            <td>{r.leaveType || '—'}</td>
                            <td>
                              {r.startDate
                                ? String(r.startDate).slice(0, 10)
                                : '—'}
                            </td>
                            <td>
                              {r.endDate ? String(r.endDate).slice(0, 10) : '—'}
                            </td>
                            <td>
                              {r.duration || (r.hour ? `${r.hour} Hours` : '—')}
                            </td>
                            <td>
                              <span className={leaveStatusClass(r.status)}>
                                {r.status || '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
